import { css, cx } from '@emotion/css';
import styled from '@emotion/styled';
import { useDebounceEffect, useMemoizedFn, useRequest } from 'ahooks';
import { InputNumber as InputNumberAntd, message, Space } from 'antd';
import CashierModal, { CashierModalMetaProps } from 'bizComponents/Cashier/CashierModal';
import CheckContainerOrg from 'components/CheckContainer';
import CurrencySelect from 'components/CurrencySelect';
import PaymentCheckGroup from 'components/PayTypeCheckGroup';
import Price from 'components/Price';
import StatusButton from 'components/StatusButton';
import useAccid from 'hooks/business/user/useAccid';
import useModal from 'hooks/useModal';
import { useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { calCoinsPaymentPirce, createCoinsOrder } from 'services/api/virtualAssets';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import usePay from 'hooks/business/pay/usePay';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

const Item = styled.div`
  margin: 10px 0 30px 0;
  > h5 {
    font-size: 16px;
  }
`;

const Plans = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  > li {
    /* line-height: 1em; */
    /* padding: 8px 12px; */
    /* border: 1px solid #ccc; */
    user-select: none;
    + li {
      margin-left: 20px;
    }
    &.actived {
      border-color: var(--fett-app-primary-color);
      color: var(--fett-app-primary-color);
    }
  }
`;
const InputNumber = styled(InputNumberAntd)`
  border: none !important;
  outline: none;
  box-shadow: none !important;
  min-width: 120px;
  input {
    height: 20px;
    min-width: 120px;
    outline: none;
  }
`;
const CheckContainer = styled(CheckContainerOrg)`
  display: flex;
  align-items: center;
  padding: 10px 20px;
`;
const CheckContainerForInput = styled(CheckContainer)`
  min-width: 100px;
`;

export interface TopupProps {
  onPayed?: () => void;
}

const plans = [
  {
    type: 'number',
    value: 50,
  },
  {
    type: 'number',
    value: 100,
  },
  {
    type: 'number',
    value: 500,
  },
  {
    type: 'number',
    value: 1000,
  },
  {
    type: 'customize',
    value: 0,
  },
] as const;

const MIN_COIN = 1;
const MAX_COIN = 5000;
const validCoins = (coins?: number) => {
  if (coins === undefined) {
    return false;
  }
  return coins >= MIN_COIN && coins <= MAX_COIN;
};
const Topup = (props: TopupProps) => {
  const { onPayed: onPayedProp } = props;
  const language = useSystemLanguageValue();
  const accid = useAccid();
  const {
    availableCurrencies,
    availablePayTypes,
    setCurrency,
    currency,
    payType,
    setPayType,
    bank,
    setBank,
  } = usePay();
  const { t } = useTranslate();
  const [selectedPlanIdx, setSelectedPlanIdx] = useState<number>(0);
  const [coins, setCoins] = useState<number | undefined>(() => plans[selectedPlanIdx].value);
  const inputNumRef = useRef<HTMLInputElement>(null);
  const { visible, meta, show, hide } = useModal<CashierModalMetaProps>(false);
  const { data: priceData, run: calcPrice } = useRequest(
    (currency: General.Currency, amount: number) => {
      return calCoinsPaymentPirce({ currency, virtualAssistantsNumber: amount }).then((r) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        const priceData = r.data.data.find((t) => t.currency === currency);
        const { price = 0 } = priceData || {};
        const pricePerCoin = price ? price / amount : 0;
        return { price, currency, pricePerCoin };
      });
    },
    { manual: true, debounceWait: 300 },
  );
  const doCreateOrder = useMemoizedFn(async () => {
    if (disabledCreation) {
      return;
    }
    await createCoinsOrder({
      balance: coins!,
      currency,
      payType,
      accId: accid || '-',
      language,
    })
      .then((r) => {
        show({
          currency,
          price: price || 0,
          config: r.data.data,
        });
      })
      .catch((e) => {
        errorToasterWithServerMsg(e);
      });
  });
  const onPayed = useMemoizedFn(() => {
    hide();
    message.success(t('ViewShops.充值成功'));
    onPayedProp?.();
  });
  useDebounceEffect(
    () => {
      if (!validCoins(coins)) {
        return;
      }
      calcPrice(currency!, coins!);
    },
    [currency, coins],
    { wait: 300 },
  );
  const disabledCreation = !validCoins(coins);
  const price = priceData?.price;
  return (
    <>
      <Item>
        <h5>{t('WhatsApp.充值点券数')}</h5>
        <Plans>
          {plans.map((plan, idx) => {
            let content = null;
            const checked = selectedPlanIdx === idx;
            const handleClick = () => {
              const coins = plan.value;
              setCoins(coins);
              setSelectedPlanIdx(idx);
            };
            switch (plan.type) {
              case 'number': {
                content = (
                  <CheckContainer onClick={handleClick} checked={checked}>
                    {t('WhatsApp.x点券', { x: plan.value })}
                  </CheckContainer>
                );
                break;
              }
              case 'customize': {
                const customizeClick = () => {
                  const coins = plan.value;
                  setCoins(coins);
                  setSelectedPlanIdx(idx);
                  inputNumRef.current?.focus?.();
                  setCoins(undefined);
                };
                content = (
                  <Space>
                    <CheckContainerForInput onClick={customizeClick} checked={checked}>
                      <InputNumber
                        ref={inputNumRef}
                        value={checked ? coins : undefined}
                        onChange={(e) => {
                          const coins = Number(e);
                          setCoins(coins);
                        }}
                        width={200}
                        controls={false}
                        placeholder={t('WhatsApp.请输入其他点数')}
                        min={MIN_COIN}
                        max={MAX_COIN}
                        step={1}
                      />
                      {t('WhatsApp.x点券', { x: '' })}
                    </CheckContainerForInput>
                    <span
                      className={css`
                        color: #999;
                      `}
                    >
                      {t('WhatsApp.每次充值上限x点券', { x: MAX_COIN })}
                    </span>
                  </Space>
                );
                break;
              }
              default: {
                content = null;
                break;
              }
            }
            return (
              <li className={cx({ actived: checked })} key={`coins-plan-${plan.value}`}>
                {content}
              </li>
            );
          })}
        </Plans>
      </Item>
      <Item>
        <h5>{t('translation.充值货币')}</h5>
        <Space>
          <CurrencySelect
            dataSource={availableCurrencies}
            value={currency}
            onChange={setCurrency}
          />

          <Space>
            <span>{t('WhatsApp.x点券', { x: 1 })}</span>
            <Price
              currency={priceData?.currency}
              value={priceData?.pricePerCoin || 0}
              type="normal"
            />
          </Space>
        </Space>
      </Item>
      <Item>
        <h5>{t('market.支付方式')}</h5>
        <Space>
          <PaymentCheckGroup
            dataSource={availablePayTypes}
            onChange={setPayType}
            value={payType}
            currency={currency}
            bank={bank}
            onBankChange={setBank}
          />
        </Space>
      </Item>
      <Item>
        <h5>{t('market.总计')}</h5>
        <Price currency={priceData?.currency} value={priceData?.price || 0} />
      </Item>
      <Item>
        <StatusButton type="primary" disabled={disabledCreation} onClick={doCreateOrder}>
          {t('WhatsApp.立即充值')}
        </StatusButton>
        <CashierModal
          onCancel={() => hide()}
          meta={meta}
          content={
            <div
              className={css`
                font-size: 22px;
              `}
            >
              {t('WhatsApp.x点券', { x: coins })}
            </div>
          }
          onRefresh={doCreateOrder}
          onPayed={onPayed}
          visible={visible}
        />
      </Item>
    </>
  );
};
export default Topup;
